"use client"

import { Button } from "@/components/ui/button"
import { Trophy } from "lucide-react"
import { ResultsActions } from "./results-actions"
import { useHeaderWidth } from "@/hooks/use-header-width"
import { useEffect, useState, type RefObject } from "react"
import type { NFTCollection } from "@/lib/api"
import MetaMaskButton from "./metamask-button"
import WalletConnectButton from "./WalletConnectButton"
import { GasPriceIndicator } from "./gas-price-indicator";
import { AnimatedDiceIcon } from "./animated-dice-icon";
import { useWallet } from '@/contexts/wallet-context'

interface MobileWalletInfoProps {
  walletAddress: string
  truncatedAddress: string
  ensName: string | null
  containerRef: RefObject<HTMLDivElement>
  collections: NFTCollection[]
  handleReset: () => void
}

export function MobileWalletInfo({
  walletAddress,
  truncatedAddress,
  ensName,
  containerRef,
  collections,
  handleReset,
}: MobileWalletInfoProps) {
  const headerWidth = useHeaderWidth()
  const [connectError, setConnectError] = useState<string | null>(null)
  const [mintError, setMintError] = useState<string | null>(null)

  const {
    provider,
    address,
    type,
    connecting,
    error: providerError,
    connectMetaMask,
    connectWalletConnect,
    disconnect,
  } = useWallet()

  const handleButtonError = (error: string) => {
    setMintError(error)
    setTimeout(() => setMintError(null), 5000)
  }



  return (
    <div className="bg-gray-100/90 backdrop-blur-md rounded-3xl px-6 py-6 relative mx-auto sm:sticky sm:top-24 z-30">
      <div>
        {/* Wallet, ENS and Blocks in grid */}
        <div className="grid grid-cols-2">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-gray-500">Wallet Address</p>
            <p className="text-sm font-mono truncate" title={walletAddress}>{truncatedAddress}</p>
          </div>
          <div className="min-w-0 border-l border-gray-200 pl-4">
            {ensName ? (
              <>
                <p className="text-xs uppercase tracking-wide text-gray-500">ENS Name</p>
                <p className="text-sm font-mono truncate">{ensName}</p>
              </>
            ) : (
              <>
                <p className="text-xs uppercase tracking-wide text-gray-500">Blocks</p>
                <p className="text-sm font-mono">{collections.length}</p>
              </>
            )}
          </div>
        </div>

        {/* Blocks section - spans full width below only when ENS exists */}
        {ensName && (
          <div className="mt-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">Blocks</p>
            <p className="text-sm font-mono">{collections.length}</p>
          </div>
        )}
      </div>

      {/* Minting buttons for mobile - full width */}
      <div className="mt-4 space-y-2">
        <div className="flex flex-col gap-2 w-full">
          {(type === 'metamask' || !type) && (
            <MetaMaskButton
              walletAddress={walletAddress}
              connectMetaMask={(...args) => connectMetaMask(...args).catch(e => setConnectError(e.message || 'MetaMask error'))}
              provider={provider}
              address={address}
              type={type}
              connecting={connecting}
              error={null}
              className="bg-[#7B39ED] hover:bg-[#6429D8] text-white font-bold w-full h-10"
              currentCollections={collections}
              tokenId={undefined}
              onError={handleButtonError}
            />
          )}
          {(type === 'walletconnect' || !type) && (
            <WalletConnectButton
              connectWalletConnect={(...args) => connectWalletConnect(...args).catch(e => setConnectError(e.message || 'WalletConnect error'))}
              disconnect={disconnect}
              provider={provider}
              address={address}
              type={type}
              connecting={connecting}
              error={null}
              className="bg-black hover:bg-gray-900 text-white font-bold w-full h-10"
              currentCollections={collections}
              tokenId={undefined}
              onError={handleButtonError}
              walletAddress={walletAddress}
            />
          )}
        </div>
        {/* Error display for mobile */}
        {(connectError || mintError) && (
          <div className="w-full text-center text-red-500 text-sm font-normal">
            {connectError || mintError}
          </div>
        )}
        
        {/* Gas Price Indicator for mobile */}
        <div className="w-full flex justify-center mt-2">
          <GasPriceIndicator />
        </div>
      </div>

      {/* Options row (mobile) */}
      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-gray-500 uppercase tracking-wide">Options</span>
        <div className="flex items-center gap-2">
          {collections.length >= 3 && (
            <Button
              variant="outline"
              size="icon"
              className="rounded-full border-gray-300 text-gray-700 hover:bg-gray-100 transition-all duration-300 w-10 h-10"
              onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', {
                key: 'r',
                code: 'KeyR',
                keyCode: 82,
                which: 82,
                bubbles: true
              }))}
              aria-label="Randomize"
            >
              <AnimatedDiceIcon dotCount={Math.floor(Math.random() * 6) + 1} />
            </Button>
          )}
          <ResultsActions
            containerRef={containerRef}
            collections={collections}
            currentCollections={collections}
            walletAddress={walletAddress}
            ensName={ensName}
            isMobile={true}
            compact
          />
        </div>
      </div>
    </div>
  )
}
